// "Minhas Tarefas" — /crm/activities/my-tasks (Story 11.8 / FR-58.5)
// - Feed de tarefas pendentes do operador logado (my_tasks=true, done=false).
// - dueDate formatado com formatBR; atrasadas (cor warning "Atrasado") e hoje
//   ("Hoje") destacadas. Filtro Todas/Atrasadas/Hoje/Esta semana (due_before).
// - Concluir inline otimista: tarefa some na hora; rollback + toast se a API falhar.
// - Skeleton no loading (AC10), empty/error states, "carregar mais".

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ListTodo, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import type { CrmActivityTypeSchema } from '@control-center/shared';
import { addDaysLocal, daysDiffLocal, formatDayBR, todayLocal } from '@control-center/shared';

import { EmptyState } from '../../../components/common/EmptyState';
import { PillButton } from '../../../components/clients/PillButton';
import {
  useCrmActivitiesInfinite,
  useUpdateCrmActivity,
  type CrmActivitiesListFilters,
} from '../../../hooks/use-crm-activities';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmActivityEntity } from '../../../lib/crm-activities-api';

type DueFilter = 'all' | 'overdue' | 'today' | 'week';

const TYPE_LABEL: Record<CrmActivityTypeSchema, string> = {
  ligacao: 'Ligação',
  mensagem: 'Mensagem',
  reuniao: 'Reunião',
  email: 'E-mail',
  nota: 'Nota',
  mudanca_estagio: 'Mudança de estágio',
  proposta_enviada: 'Proposta enviada',
  tarefa: 'Tarefa',
};

const FILTERS: { value: DueFilter; label: string }[] = [
  { value: 'all', label: 'Todas' },
  { value: 'overdue', label: 'Atrasadas' },
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Esta semana' },
];

/** Constrói o filtro de data conforme a aba. `due_before` é inclusivo (<=). */
function buildFilters(filter: DueFilter): CrmActivitiesListFilters {
  const base: CrmActivitiesListFilters = { my_tasks: true, done: false };
  const today = todayLocal();
  if (filter === 'overdue') return { ...base, due_before: addDaysLocal(today, -1) };
  if (filter === 'today') return { ...base, due_before: today };
  if (filter === 'week') return { ...base, due_before: addDaysLocal(today, 7) };
  return base;
}

export function MyTasksPage(): JSX.Element {
  const [filter, setFilter] = useState<DueFilter>('all');
  // IDs concluídos de forma otimista — escondidos imediatamente da lista (AC10).
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());

  const filters = useMemo(() => buildFilters(filter), [filter]);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useCrmActivitiesInfinite(filters);

  const update = useUpdateCrmActivity();

  const tasks = useMemo(
    () => (data?.pages ?? []).flatMap((p) => p.items).filter((t) => !completedIds.has(t.id)),
    [data, completedIds],
  );

  const handleComplete = (task: CrmActivityEntity): void => {
    // Otimista: esconde já.
    setCompletedIds((prev) => new Set(prev).add(task.id));

    update.mutate(
      { id: task.id, payload: { done: true } },
      {
        onSuccess: () => {
          toast.success('Tarefa concluída.', { description: task.subject });
        },
        onError: (err) => {
          // Rollback visual — devolve a tarefa pra lista.
          setCompletedIds((prev) => {
            const next = new Set(prev);
            next.delete(task.id);
            return next;
          });
          toast.error('Não conseguimos concluir.', {
            description: extractErrorMessage(err, 'Tente de novo em instantes.'),
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
            Minhas tarefas
          </h1>
          <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
            Seus follow-ups agendados — não deixe nenhum lead esfriar por esquecimento.
          </p>
        </div>
        <PillButton
          onClick={() => refetch()}
          icon={
            <RefreshCw
              size={14}
              strokeWidth={2}
              className={isFetching ? 'animate-spin' : undefined}
            />
          }
        >
          Atualizar
        </PillButton>
      </header>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={`rounded-[8px] border px-3 py-[6px] text-[12px] font-medium transition-colors ${
              filter === f.value
                ? 'border-line-2 bg-panel-2 text-ink'
                : 'border-line bg-panel text-muted hover:text-ink'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <TaskSkeleton />
      ) : isError ? (
        <TaskError
          message={extractErrorMessage(error, 'Não conseguimos carregar suas tarefas.')}
          onRetry={() => refetch()}
        />
      ) : tasks.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title="Nenhuma tarefa pendente"
          description="Tudo em dia por aqui. Crie tarefas na timeline de um lead, deal ou cliente pra agendar follow-ups."
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                isCompleting={update.isPending}
                onComplete={() => handleComplete(task)}
              />
            ))}
          </ul>
          {hasNextPage && (
            <div className="flex justify-center">
              <PillButton onClick={() => void fetchNextPage()} disabled={isFetchingNextPage}>
                {isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
              </PillButton>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// =============================================================================
// Linha de tarefa
// =============================================================================

interface TaskRowProps {
  task: CrmActivityEntity;
  isCompleting: boolean;
  onComplete: () => void;
}

/** Classifica a tarefa pela data de vencimento (no fuso BR). */
function dueStatus(dueDate: string | null): 'overdue' | 'today' | 'future' | 'none' {
  if (!dueDate) return 'none';
  const diff = daysDiffLocal(todayLocal(), dueDate); // dueDate - hoje
  if (diff < 0) return 'overdue';
  if (diff === 0) return 'today';
  return 'future';
}

function TaskRow({ task, isCompleting, onComplete }: TaskRowProps): JSX.Element {
  const status = dueStatus(task.due_date);

  const targetHref = task.target?.kind === 'lead' ? `/crm/leads/${task.target.id}` : undefined;

  return (
    <li className="flex items-start gap-3 rounded-[12px] border border-line bg-panel px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-3">
            {TYPE_LABEL[task.type]}
          </span>
          <DueBadge status={status} dueDate={task.due_date} />
        </div>
        <p className="text-[13px] font-medium leading-snug text-ink">{task.subject}</p>
        {task.target && (
          <p className="text-[11px] text-muted-2">
            {task.target.kind === 'lead'
              ? 'Lead'
              : task.target.kind === 'deal'
                ? 'Deal'
                : 'Cliente'}
            :{' '}
            {targetHref ? (
              <Link to={targetHref} className="text-ink-2 underline-offset-2 hover:underline">
                {task.target.name}
              </Link>
            ) : (
              <span className="text-ink-2">{task.target.name}</span>
            )}
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={onComplete}
        disabled={isCompleting}
        aria-label={`Concluir tarefa ${task.subject}`}
        className="inline-flex h-8 shrink-0 items-center gap-[6px] rounded-[8px] border border-line px-3 text-[12px] font-medium text-muted transition-colors hover:border-ok/50 hover:bg-ok/10 hover:text-ok disabled:opacity-50"
      >
        <Check size={14} strokeWidth={2.5} />
        Concluir
      </button>
    </li>
  );
}

function DueBadge({
  status,
  dueDate,
}: {
  status: ReturnType<typeof dueStatus>;
  dueDate: string | null;
}): JSX.Element | null {
  if (status === 'none') return null;
  if (status === 'overdue') {
    return (
      <span className="rounded-[5px] border border-bad/40 px-[6px] py-[1px] text-[10px] font-medium text-bad">
        Atrasado · {formatDayBR(dueDate)}
      </span>
    );
  }
  if (status === 'today') {
    return (
      <span className="rounded-[5px] border border-warn/40 px-[6px] py-[1px] text-[10px] font-medium text-warn">
        Hoje
      </span>
    );
  }
  return (
    <span className="rounded-[5px] border border-line px-[6px] py-[1px] text-[10px] text-muted">
      Vence {formatDayBR(dueDate)}
    </span>
  );
}

// =============================================================================
// States
// =============================================================================

function TaskSkeleton(): JSX.Element {
  return (
    <ul className="flex flex-col gap-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-[12px] border border-line bg-panel px-4 py-[14px]"
        >
          <div className="flex flex-1 flex-col gap-2">
            <div className="h-3 w-24 animate-pulse rounded bg-panel-2" />
            <div className="h-3 w-56 animate-pulse rounded bg-panel-2" />
          </div>
          <div className="h-8 w-24 animate-pulse rounded-[8px] bg-panel-2" />
        </li>
      ))}
    </ul>
  );
}

function TaskError({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar suas tarefas.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
